import { getFormValues, formValueSelector } from 'redux-form';
import { createSelector } from 'reselect';
import { get, maxBy } from 'lodash';

import { IMPORT_INGREDIENT_FORM_NAME } from 'Common/FormEnum';
import { ImportIngredientsFieldEnum as Fields } from 'Common/FieldEnum';
import { makeOptionsData } from 'Common/FormHelper';
import { createMap } from 'Common/Helpers/format';
import { IMPORT_TYPE_MAP } from 'Common/Constants';
import { SKIP } from 'Common/Components/ImportPreview/helpers/Constants';
import { generateHeaderList } from 'Common/Components/ImportPreview/helpers';

const formValuesSelector = getFormValues(IMPORT_INGREDIENT_FORM_NAME);

const root = state => get(state, 'ImportIngredientState');

export const getImportIngFormValues = state => formValuesSelector(state) || {};

export const getIngredientColumns = state =>
  get(root(state), 'ingredientColumns', []);

export const getIngredientOptions = createSelector(
  getIngredientColumns,
  ingredientColumns => {
    const options = makeOptionsData(ingredientColumns, {
      keyKey: Fields.ColumnName,
      valueKey: Fields.ColumnLabel,
      textKeys: [Fields.ColumnLabel],
    });

    options.splice(0, 0, {
      value: SKIP,
      key: SKIP,
      text: SKIP,
    });

    return options;
  }
);

export const getFetching = state => get(root(state), 'fetching');

export const getFetchError = state => get(root(state), 'fetchError');

export const getDelimiter = state => {
  const formValues = getImportIngFormValues(state);
  return formValues[Fields.Delimiter] || '';
};

export const getPreviewData = state => get(root(state), 'previewData');

export const getFileDataForPreview = createSelector(
  getIngredientColumns,
  getPreviewData,
  (ingredientColumns, previewData) => {
    const numberOfColumns = ingredientColumns.length;

    // Only show up to the columns that we have
    return previewData.map(row => row.slice(0, numberOfColumns));
  }
);

export const getSavedImportConfigs = state =>
  get(root(state), 'savedImportConfigs');

export const getSavedImportConfigsMap = createSelector(
  getSavedImportConfigs,
  savedImportConfigs => {
    return createMap(savedImportConfigs);
  }
);

export const getSavedImportOptions = createSelector(
  getSavedImportConfigs,
  savedImportConfigs => {
    return makeOptionsData(savedImportConfigs, {
      keyKey: 'Id',
      textKeys: ['Name'],
      valueKey: 'Id',
    });
  }
);

export const getIngredientColsMappedByColLabel = createSelector(
  getIngredientColumns,
  ingredientColumns => {
    return createMap(ingredientColumns, Fields.ColumnLabel);
  }
);

export const getSelectedFieldList = state => {
  const formValues = getImportIngFormValues(state);

  return formValues[Fields.SelectedFieldList];
};

export const getSelectedFieldListForAPI = state => {
  const selectedFieldList = getSelectedFieldList(state);
  const ingredientColsMappedByColLabel = getIngredientColsMappedByColLabel(
    state
  );
  const numberOfColumns = getIngredientColumns(state).length;

  const listForApi = [];

  for (let i = 0; i < numberOfColumns; i++) {
    const field = selectedFieldList[i];

    if (field) {
      listForApi.push({
        [Fields.ColumnNumber]: field[Fields.ColumnNumber],
        [Fields.ColumnName]: get(
          ingredientColsMappedByColLabel,
          [field[Fields.ColumnLabel], Fields.ColumnName],
          SKIP
        ),
        [Fields.ColumnLabel]: field[Fields.ColumnLabel],
      });
    } else {
      listForApi.push({
        [Fields.ColumnNumber]: i,
        [Fields.ColumnName]: SKIP,
        [Fields.ColumnLabel]: SKIP,
      });
    }
  }

  return listForApi;
};

export const shouldLoadSavedConfig = state => {
  const formValues = getImportIngFormValues(state);

  return formValues[Fields.ImportType] === IMPORT_TYPE_MAP.LOAD_SAVED.value;
};

export const getLoadedConfigId = state => {
  const formValues = getImportIngFormValues(state);

  return formValues[Fields.LoadedConfigId];
};

export const getLongestFileLineLength = createSelector(
  getPreviewData,
  previewData => {
    const longestLine = maxBy(previewData, line => line.length) || [];

    return longestLine.length;
  }
);

export const getDefaultSelectedFieldList = createSelector(
  getIngredientColumns,
  ingredientColumns => {
    return generateHeaderList(ingredientColumns, ingredientColumns.length);
  }
);

const ingredientFormSelector = formValueSelector(IMPORT_INGREDIENT_FORM_NAME);

export const getHasHeaderInfo = state =>
  ingredientFormSelector(state, Fields.IncludeHeaders);



// WEBPACK FOOTER //
// ./src/pages/importingredients/state/selectors.js